import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Helsingborg?", a: "Ansökan görs till Socialförvaltningen / tillståndsenheten i Helsingborgs stad. Verksamhet i annan kommun i Skåne län (t.ex. Höganäs, Ängelholm, Bjuv, Landskrona) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Helsingborg?", a: "Helsingborg kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Helsingborg?", a: "Helsingborg kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-helsingborg")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Helsingborg" },
      ]}
      title="Serveringstillstånd Helsingborg – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Helsingborg: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.helsingborg, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Helsingborgs stad – Tillstånd och uppföljning (näringslivsservice)",
        address: "Helsingborg kontaktcenter, Stortorget 17, 251 89 Helsingborg",
        phone: "042-10 30 30 (kommunvägledare)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd – se kommunens sida för aktuella handläggningstider",
        feeExamples: [
          "Ansöknings- och tillsynsavgifter enligt fastställd taxa – se kommunens sida för aktuella belopp",
          "Avgiften varierar med tillståndstyp och alkoholomsättning",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.helsingborg,
      }}
      sections={[
        { h2: "Var ansöker du i Helsingborg?", body: "Socialförvaltningen / tillståndsenheten i Helsingborgs stad ansvarar för serveringstillstånd i Helsingborg kommun. För verksamhet i kringliggande kommuner som Höganäs, Ängelholm, Bjuv, Landskrona ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Helsingborg kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Helsingborg har ett livligt restaurang- och hamnliv samt återkommande evenemang (t.ex. H22) som påverkar tillfälliga tillstånd." },
        { h2: "Kunskapsprovet i Helsingborg", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Handläggningstid i Helsingborg", body: "Helsingborgs stad publicerar egna sidor med aktuella handläggningstider för olika tillståndstyper, och en kommunvägledare på 042-10 30 30 kan hjälpa till med frågor om en pågående ansökan. Räkna med ca 8–12 veckor för ett stadigvarande tillstånd från komplett ansökan. Helsingborgs läge vid Öresund och återkommande stora evenemang gör att sommarsäsongen ofta innebär fler tillfälliga tillstånd och något längre handläggning." },
        { h2: "Tillsyn och uppföljning", body: "Helsingborg kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Guide: serveringstillstånd i Helsingborg 2026" },
      { name: "description", content: "Allt du behöver veta om serveringstillstånd i Helsingborg 2026: ansökan, krav och kunskapsprovet i alkohollagen." },
      { property: "og:title", content: "Guide – serveringstillstånd Helsingborg" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Helsingborg." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-helsingborg" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-helsingborg" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Helsingborg", url: "https://alkoholprovet.se/serveringstillstand-helsingborg" },
      ]),
    ],
  }),
});
